/**
 * The properties a drawn map feature carries, ready to show.
 *
 * Which fields exist is the dataset's business, not ours — a protected-areas layer carries
 * `designation`, `id`, `name` and `type`, a habitat layer something else entirely — so nothing
 * here names or expects any of them. That is what makes it usable by any map that wants to put
 * a feature's own properties in front of a reader.
 */
export type FeatureField = {
    label: string;
    value: string;
};

const asText = (value: unknown): string => (typeof value === 'string' ? value : JSON.stringify(value) ?? '');

export const fieldsOf = (properties: Record<string, unknown> | null | undefined): FeatureField[] =>
    Object.entries(properties ?? {})
        .map(([label, value]) => ({ label, value: value === null || value === undefined ? '' : asText(value) }))
        // A field with nothing in it says nothing, and spends a line of the popup saying it.
        .filter(field => field.value.trim().length > 0)
        // Alphabetical: the order a vector tile hands its properties back in is neither the
        // producer's nor stable, so a popup would otherwise reshuffle as the mouse moves.
        .sort((a, b) => a.label.localeCompare(b.label));
